import { ConfirmCloseModal, useModal, useRightPane } from "@homebound/beam";
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { TakeoffLinePanel } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/TakeoffLinePanel";
import { TakeoffsBulkEditPanel } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/TakeoffsBulkEditPanel";
import {
  TakeoffsSidePanelContext,
  TakeoffsSidePanelContextValue,
  TakeoffsSidePanelMode,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/useTakeoffsSidePanel";
import { LineType } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/utils";

/**
 * Which line the side panel shows, and its unsaved-changes check. Kept out of the takeoffs store, which a save to a new
 * draft rebuilds.
 */
export function TakeoffsSidePanelProvider(props: PropsWithChildren) {
  const { children } = props;
  const { openRightPane, closeRightPane, isRightPaneOpen } = useRightPane();
  const { openModal } = useModal();
  const [shownTliId, setShownTliId] = useState<string>();
  // Beam also closes the pane itself when its host unmounts, e.g. leaving the tab
  const activeTliId = isRightPaneOpen ? shownTliId : undefined;
  // Read through a ref so the actions don't change identity on every open
  const activeTliIdRef = useRef(activeTliId);
  useEffect(() => {
    activeTliIdRef.current = activeTliId;
  }, [activeTliId]);
  const isDirtyRef = useRef<() => boolean>();
  const openCountRef = useRef(0);

  const actions = useMemo(() => {
    function show(mode: TakeoffsSidePanelMode) {
      setShownTliId(mode.kind === "edit" ? mode.tliId : undefined);
      // A new key per open, so even re-opening the same Add form starts fresh
      openCountRef.current++;
      openRightPane({
        content:
          mode.kind === "bulkEdit" ? (
            <TakeoffsBulkEditPanel key={openCountRef.current} />
          ) : (
            <TakeoffLinePanel key={openCountRef.current} mode={mode} />
          ),
      });
    }
    function close() {
      setShownTliId(undefined);
      closeRightPane();
    }
    function confirmDiscard(proceed: VoidFunction) {
      if (isDirtyRef.current?.()) {
        openModal({ content: <ConfirmCloseModal title="Discard unsaved changes?" onClose={proceed} /> });
      } else {
        proceed();
      }
    }
    return {
      openEdit: (tliId: string) => {
        if (tliId !== activeTliIdRef.current) confirmDiscard(() => show({ kind: "edit", tliId }));
      },
      openAdd: (type: LineType) => confirmDiscard(() => show({ kind: "add", type })),
      openDuplicate: (tliId: string) => confirmDiscard(() => show({ kind: "duplicate", tliId })),
      openBulkEdit: () => confirmDiscard(() => show({ kind: "bulkEdit" })),
      closePanel: () => confirmDiscard(close),
      dismissPanel: close,
      showLine: (tliId: string) => show({ kind: "edit", tliId }),
      lineDeleted: (tliId: string) => {
        if (tliId === activeTliIdRef.current) close();
      },
      registerDirtyCheck: (isDirty: () => boolean) => {
        isDirtyRef.current = isDirty;
        return () => {
          if (isDirtyRef.current === isDirty) isDirtyRef.current = undefined;
        };
      },
    } satisfies Omit<TakeoffsSidePanelContextValue, "activeTliId">;
  }, [openRightPane, closeRightPane, openModal]);

  const value: TakeoffsSidePanelContextValue = useMemo(() => ({ ...actions, activeTliId }), [actions, activeTliId]);
  return <TakeoffsSidePanelContext.Provider value={value}>{children}</TakeoffsSidePanelContext.Provider>;
}
